import { Component, computed, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/services/auth.service';
import { CartService } from '../../../core/services/cart.service';
import { StoreService } from '../../../core/services/store.service';
import { TenantService } from '../../../core/services/tenant.service';
import { ThemeService } from '../../../core/services/theme.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, ButtonModule, MenuModule, OverlayBadgeModule],
  template: `
    <header class="sticky top-0 z-50 border-b border-surface-200/70 bg-surface-0/75 backdrop-blur-xl backdrop-saturate-150">
      <div class="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
        <!-- Marca: o nome da loja na vitrine dela; a plataforma no resto -->
        @if (inStore()) {
          <a [routerLink]="storeService.link()" class="flex min-w-0 items-center gap-2.5 text-base font-bold text-surface-900 sm:text-lg">
            <span class="flex size-9 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-primary-400 to-primary-600 text-white shadow-md shadow-primary-500/25">
              <i class="pi pi-shop"></i>
            </span>
            <span class="truncate">{{ storeService.store()?.name }}</span>
          </a>
        } @else {
          <a routerLink="/" class="flex items-center gap-2.5 whitespace-nowrap text-base font-extrabold text-surface-900 sm:text-lg">
            <img src="logo.png" alt="" class="h-8 w-auto" />
            <span class="text-primary dark:text-surface-900">{{ platformName }}</span>
          </a>
        }

        <!-- Atalhos para as seções da landing page -->
        @if (onLanding()) {
          <nav class="hidden items-center gap-1 md:flex" aria-label="Seções da página">
            @for (section of landingSections; track section.id) {
              <a routerLink="/" [fragment]="section.id"
                 class="rounded-lg px-3 py-2 text-sm font-medium text-surface-600 transition-colors hover:bg-surface-100 hover:text-surface-900">
                {{ section.label }}
              </a>
            }
          </nav>
        }

        <nav class="flex items-center gap-1 sm:gap-3">
          <!-- Carrinho (só na loja) -->
          @if (inStore()) {
            <a [routerLink]="storeService.link('checkout')" aria-label="Carrinho"
               class="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-surface-700 transition-colors hover:bg-surface-100">
              @if (cartService.count() > 0) {
                <p-overlaybadge [value]="cartService.count()" severity="danger">
                  <i class="pi pi-shopping-cart text-xl"></i>
                </p-overlaybadge>
              } @else {
                <i class="pi pi-shopping-cart text-xl"></i>
              }
              <span class="hidden sm:inline">Carrinho</span>
            </a>
          }

          <!-- Alternância entre tema claro e escuro -->
          <p-button [icon]="themeService.isDark() ? 'pi pi-sun' : 'pi pi-moon'" severity="secondary" [text]="true" [rounded]="true"
                    [ariaLabel]="themeService.isDark() ? 'Mudar para o tema claro' : 'Mudar para o tema escuro'"
                    (onClick)="themeService.toggle()" />

          <!-- Área de Utilizador Autenticado -->
          @if (authService.isAuthenticated()) {
            <p-button [label]="firstName()" icon="pi pi-user" severity="secondary" [outlined]="true"
                      (onClick)="userMenu.toggle($event)" />
            <p-menu #userMenu [model]="menuItems()" [popup]="true" appendTo="body" />
          }
          <!-- Administrador vendo a própria loja: volta para o painel em vez de pedir login -->
          @else if (isOwnStoreAdmin()) {
            <p-button label="Painel" icon="pi pi-cog" routerLink="/admin" />
          }
          <!-- Área de Visitante -->
          @else if (inStore()) {
            <p-button label="Entrar" icon="pi pi-sign-in" (onClick)="goToLogin()" />
          } @else {
            <p-button label="Entrar" severity="secondary" [text]="true" routerLink="/entrar" />
            <p-button label="Teste grátis" icon="pi pi-arrow-right" iconPos="right" routerLink="/cadastro" [rounded]="true" class="hidden sm:block" />
          }
        </nav>
      </div>
    </header>
  `
})
export class HeaderComponent {
  authService = inject(AuthService);
  cartService = inject(CartService);
  storeService = inject(StoreService);
  themeService = inject(ThemeService);
  private tenantService = inject(TenantService);
  private router = inject(Router);

  platformName = environment.platformName;

  landingSections = [
    { id: 'beneficios', label: 'Recursos' },
    { id: 'como-funciona', label: 'Como funciona' },
    { id: 'planos', label: 'Planos' },
    { id: 'duvidas', label: 'Dúvidas' }
  ];

  // true na página inicial da plataforma (mostra os atalhos das seções)
  onLanding = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url.split(/[?#]/)[0] === '/')
    ),
    { initialValue: location.pathname === '/' }
  );

  // true na vitrine de uma loja (/:slug); false na plataforma e no painel
  inStore = computed(() => this.authService.storeSlug() !== null);

  // true quando o administrador logado no painel está na vitrine da própria empresa
  isOwnStoreAdmin = computed(() => {
    const slug = this.authService.storeSlug();
    return slug !== null && this.authService.adminUser()?.tenant.slug === slug;
  });

  // Mostra apenas o primeiro nome
  firstName = computed(() => this.authService.user()?.name?.split(' ')[0] || 'Perfil');

  menuItems = computed<MenuItem[]>(() => {
    const user = this.authService.user();
    const logout: MenuItem[] = [{ separator: true }, { label: 'Sair', icon: 'pi pi-sign-out', command: () => this.logout() }];

    // Administrador (plataforma/painel)
    if (!this.inStore()) {
      return [
        { label: 'Painel', icon: 'pi pi-cog', routerLink: '/admin' },
        { label: 'Ver minha loja', icon: 'pi pi-shop', routerLink: ['/', user?.tenant.slug ?? ''] },
        { label: 'Minha conta', icon: 'pi pi-user-edit', routerLink: '/admin/account' },
        ...logout
      ];
    }

    // Cliente da loja ("Meus pedidos" só existe nos planos com acompanhamento do pedido)
    return [
      ...(this.storeService.hasModule('ORDER_TRACKING')
        ? [{ label: 'Meus pedidos', icon: 'pi pi-receipt', routerLink: this.storeService.link('my-orders') }]
        : []),
      { label: 'Meus dados', icon: 'pi pi-user-edit', routerLink: this.storeService.link('profile') },
      ...logout
    ];
  });

  goToLogin() {
    this.router.navigate(this.storeService.link('login'));
  }

  logout() {
    const inStore = this.inStore();
    this.authService.logout();
    if (!inStore) this.tenantService.clear();
    // O cliente volta ao cardápio da loja; o administrador, à página inicial da plataforma
    this.router.navigate(inStore ? this.storeService.link() : ['/']);
  }
}
